import { format, parse, isToday, isTomorrow, isYesterday } from 'date-fns';

export const getOrdinalSuffix = (date: Date): string => {
    const day = date.getDate();
    switch (day) {
        case 1:
        case 21:
        case 31:
            return `${day}st`;
        case 2:
        case 22:
            return `${day}nd`;
        case 3:
        case 23:
            return `${day}rd`;
        default:
            return `${day}th`;
    }
};

/**
 * Convert date into human readable date time
 * @date 26/09/2023
 *
 * @param {string} dateString
 * @returns {*}
 */
export const formatDate = (
    dateString: string,
    enableRelativeNaming: boolean = false,
    shortDayFormat: boolean = false
) => {
    if (!dateString) return;

    // Parse the input date string to a Date object
    const date = parse(dateString, 'yyyy-MM-dd', new Date());

    // Check if the date is today, tomorrow, or yesterday
    if (enableRelativeNaming) {
        if (isToday(date)) {
            return 'Today';
        } else if (isTomorrow(date)) {
            return 'Tomorrow';
        } else if (isYesterday(date)) {
            return 'Yesterday';
        }
    }

    // eeee format does the day
    // Format the date as "eeee do MMMM"
    const dayFormat = shortDayFormat ? 'eee' : 'eeee';
    const formattedDate = format(date, `${dayFormat} do MMMM`);
    return formattedDate;
};

export const dayFromDate = (
    dateString: string,
    enableRelativeNaming: boolean = false,
    returnDayFormat: boolean = true // This flag will decide whether to return day or date (e.g., 13th, 14th)
) => {
    const date = parse(dateString, 'yyyy-MM-dd', new Date());

    // Check if the date is today, tomorrow, or yesterday
    if (enableRelativeNaming) {
        if (isToday(date)) {
            return 'Today';
        } else if (isTomorrow(date)) {
            return 'Tomorrow';
        } else if (isYesterday(date)) {
            return 'Yesterday';
        }
    }

    if (returnDayFormat) {
        // Return short day format (Tue, Wed, etc.)
        return format(date, 'EEE');
    } else {
        // Return the date in 13th, 14th, etc. format
        return format(date, 'do');
    }
};

export function getFormattedDate(inputDate?: Date | number): string {
    // Check if inputDate is provided and is a valid date or timestamp
    let date;
    if (inputDate) {
        if (typeof inputDate === 'number') {
            // it's a timestamp
            date = new Date(inputDate);
        } else if (inputDate instanceof Date) {
            // it's a Date object
            date = inputDate;
        } else {
            throw new Error(
                'Invalid inputDate provided. Expected a Date object or Unix timestamp.'
            );
        }
    } else {
        date = new Date(); // use current date if no inputDate is provided
    }

    const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    const dayWithSuffix = getOrdinalSuffix(date);
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${dayWithSuffix} ${month}, ${year}`;
}

// Helper function to convert time to 12-hour format
export const convertTo12Hour = (time) => {
    if (!time) {
        return;
    }
    let [hour, minute] = time.split(':').map(Number);
    const suffix = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${String(hour)}:${String(minute).padStart(2, '0')} ${suffix}`;
};

// Helper function to format time to hh:mm
export const formatTimeToHHMM = (time) => {
    const timeParts = time.split(':');
    let hours = parseInt(timeParts[0]);
    let minutes = timeParts[1];

    // Ensure hours are two digits
    if (hours < 10) {
        hours = '0' + hours;
    }

    return `${hours}:${minutes}`;
};
export const getMonthYear = (dateStr) => {
  const date = new Date(dateStr);

  return date.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });
};

export const formatTimeIntoHHMM = (timeStr: string): string => {
  if (!timeStr) return timeStr; // fallback

  const date = new Date(`1970-01-01T${timeStr}`);
  if (isNaN(date.getTime())) {
    // invalid input, return original string
    return timeStr;
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};
